<template>
    <SmartLinkItemComponent :id="model?.system?.id" :component="!model?.system?.workflow" v-if="model">
        <div class="publication-list">
            <div class="container">
                <div class="row">

                    <div class="col-lg-3">
                        <div class="filter-sidebar" data-aos="fade-in">
                            <div class="news-type">
                                <div class="filter-title" @click="toggleNewsType">
                                    <span class="text">News Type</span>
                                    <IconChevronUp v-if="expandNewsType" class="icon" />
                                    <IconChevronDown v-else class="icon" />
                                </div>
                                <div class="filter-list" v-if="expandNewsType">
                                    <div class="form-check" >
                                        <input class="form-check-input" id="publications" name="publications" v-model="filters.publication.value" type="checkbox" :value="filters.publication.value">
                                        <label class="form-check-label" for="publications">Publications</label>
                                    </div>
                                    <div class="form-check" >
                                        <input class="form-check-input" id="pressRelease" name="pressRelease" v-model="filters.pressRelease.value" type="checkbox" :value="filters.pressRelease.value">
                                        <label class="form-check-label" for="pressRelease">Press Releases</label>
                                    </div>
                                    <div class="form-check" >
                                        <input class="form-check-input" id="conference" name="conference" v-model="filters.conference.value" type="checkbox" :value="filters.conference.value">
                                        <label class="form-check-label" for="conference">Conferences</label>
                                    </div>
                                </div>
                            </div>
                            <div class="sort-by">
                                <div class="filter-title" @click="toggleSortBy">
                                    <span class="text">Sort By</span>
                                    <IconChevronUp v-if="expandSortBy" class="icon" />
                                    <IconChevronDown v-else class="icon" />
                                </div>
                                <div class="filter-list" v-if="expandSortBy">
                                    <div class="form-check">
                                        <input class="form-check-input" @change="onSortChange" id="newest" name="newest" v-model="sort" type="radio" :value="0">
                                        <label class="form-check-label" for="newest">Newest First</label>
                                    </div>
                                    <div class="form-check" >
                                        <input class="form-check-input" @change="onSortChange" id="oldest" name="oldest" v-model="sort" type="radio" :value="1">
                                        <label class="form-check-label" for="oldest">Oldest First</label>
                                    </div>
                                    <div class="form-check">
                                        <input class="form-check-input" @change="onSortChange" id="a-z" name="a-z" v-model="sort" type="radio" :value="2">
                                        <label class="form-check-label" for="a-z">Title A-Z</label>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                

                    <div class="col-lg-9">
                        <div class="content">
                            <PublicationListContainer v-if="!isFiltering && dataSource.highlights.value.length > 0" :model="dataSource.highlights"
                                :increment="model?.elements?.number_of_items_to_display?.value ?? defaultLoadValue" :title="'Highlights'" />
                            <PublicationListContainer v-if="(!isFiltering || filters.publication.value) && dataSource.publications.value.length > 0"
                                :model="dataSource.publications"
                                :increment="model?.elements?.number_of_all_publications_to_display?.value ?? defaultLoadValue"
                                :title="'All Publications'" />
                            <PublicationListContainer v-if="(!isFiltering || filters.pressRelease.value) && dataSource.pressReleases.value.length > 0"
                                :model="dataSource.pressReleases"
                                :increment="model?.elements?.number_of_press_releases_to_display?.value ?? defaultLoadValue"
                                :title="'All Press Releases'" />
                            <PublicationListContainer v-if="(!isFiltering || filters.conference.value) && dataSource.conferences.value.length > 0"
                                :model="dataSource.conferences"
                                :increment="model?.elements?.number_of_conferences_to_display?.value ?? defaultLoadValue"
                                :title="'All Conferences'" />
                        </div>
                    </div>

                </div>
            </div>
        </div>
    </SmartLinkItemComponent>
</template>

<script lang="ts" setup>
import { contentTypes, type NewsMediaListModel, type PublicationItemModel } from '~~/app/models';
import type { PublicationListDataSource } from '~~/app/types/PublicationListDataSource';
import type { ApiResponse } from '~~/app/types/shared/ApiResponse'

const props = defineProps<{ model: NewsMediaListModel }>();
const defaultLoadValue = 3;
const dataSource:PublicationListDataSource = {
    all: ref<PublicationItemModel[]>([]),
    showAllCount: ref(props?.model?.elements?.number_of_items_to_display?.value ?? 0),
    highlights: ref<PublicationItemModel[]>([]),
    showHighlightsCount: ref(props?.model?.elements?.number_of_items_to_display?.value ?? 0),
  publications: ref<PublicationItemModel[]>([]),
    pressReleases: ref<PublicationItemModel[]>([]),
    showPressReleasesCount: ref(props?.model?.elements?.number_of_press_releases_to_display?.value ?? 0),
    conferences: ref<PublicationItemModel[]>([]),
    showConferenceCount: ref(props?.model?.elements?.number_of_conferences_to_display?.value ?? 0),
}
const filters = {
    publication: ref(false),
    pressRelease: ref(false),
    conference: ref(false)
};

const sort = ref<number>();
const result = ref<PublicationItemModel[]>([]);
const isFiltering = computed(() => filters.conference.value || filters.pressRelease.value || filters.publication.value);
const response = await $fetch<ApiResponse<any>>('/api/publication-list');
if(response.data && response.data.item){
  result.value = response.data.item;
  setup(result.value);
}

const sortFunctions: { [key:number]:any} = {
    [0]: (a:PublicationItemModel, b:PublicationItemModel) => getDateAsNumber(a.elements.publication_date.value) - getDateAsNumber(b.elements.publication_date.value),
    [1]: (a:PublicationItemModel, b:PublicationItemModel) => getDateAsNumber(b.elements.publication_date.value) - getDateAsNumber(a.elements.publication_date.value),
    [2]: (a:PublicationItemModel, b:PublicationItemModel) => a.elements.publication_title.value > b.elements.publication_title.value ? 1 : -1
}


function onSortChange() {
    if (sort.value || sort.value == 0){
        const sortedSrc = result.value.sort(sortFunctions[sort.value]);
        if (sortedSrc){
            setup(sortedSrc);
        }
    }
}

function setup(source: PublicationItemModel[]) {
    dataSource.all.value = source;
    dataSource.highlights.value = source?.filter(x => props.model.elements.highlights.value.includes(x.system.codename));
  dataSource.publications.value = source?.filter(x => firstOrDefault(x.elements.publication_type.value)?.codename == 'publications');
    dataSource.pressReleases.value = source?.filter(x => firstOrDefault(x.elements.publication_type.value)?.codename == 'press_release');
    dataSource.conferences.value = source?.filter(x => firstOrDefault(x.elements.publication_type.value)?.codename == 'conference');


}

// Sidebar Filter Toggle
const expandSortBy = ref(true)

const expandNewsType = ref(true)

const toggleSortBy = () => {
    expandSortBy.value = !expandSortBy.value
}

const toggleNewsType = () => {
    expandNewsType.value = !expandNewsType.value
}
</script>